import React, { useCallback, useState } from 'react'
import PropTypes from 'prop-types'
import { useDispatch, useSelector } from 'react-redux'
import { useApolloClient, useMutation, useQuery } from 'react-apollo'
import { Redirect } from 'react-router-dom'

import ConfirmationModal from '../../../components/shared/confirmation-modal'
import Button from '../../../components/button'
import { LoadingModal } from '../../../components/loading/modal'
import ProfileTile from '../components/profile-tile'
import AddProfileButton from '../components/add-profile-button'
import {
  requirePin, showPlanUpgradePopup
} from '../../../actions'
import { switchProfile, switchProfileWithPin } from '../../../actions/profile'
import { MODALS, PROFILES } from '../../../constants'
import { useIsAuthenticatedAndRedirect } from '../../../hooks/use-is-authenticated-and-redirect'
import { useMutateAndLoading } from '../../../hooks/use-mutate-and-loading'
import { useProfilesEntitlement } from '../../../hooks/useProfilesEntitlement'
import { getModalLocation } from '../../../lib/modal'
import { isPinValid } from '../../../lib/pin'
import {
  requirePinForManageProfile,
  requirePinForSwitchProfile
} from '../../../lib/account'
import { hasHigherParentalRating } from '../../../lib/utils'
import { segmentTrackDeleteProfile } from '../../../segment/segment-track'
import { getProfileIsDisabled } from '../../../utils/getProfileIsDisabled'
import ACCOUNT_QUERY from '../../../../graphql/queries/account.gql'
import DELETE_PROFILE_MUTATION from '../../../../graphql/mutations/delete-profile.gql'
import styles from './manage-profiles.css'

/** Displays controls for adding, editing, deleting profiles. */
function ManageProfiles({ history, location }) {
  const dispatch = useDispatch()
  const apolloClient = useApolloClient()
  const hasValidPin = useSelector(state => isPinValid(state))
  const [isConfirmationModalOpen, setDisplayConfirmationModal] = useState(false)
  const [selectedProfileId, setSelectedProfileId] = useState(null)
  const [currentActiveEdit, setCurrentActiveEdit] = useState(null)
  const {
    isAuthenticated,
    shouldRedirect,
    redirectTo
  } = useIsAuthenticatedAndRedirect(location)
  const accountQuery = useQuery(ACCOUNT_QUERY, {
    skip: !isAuthenticated
  })
  const [deleteProfileMutation] = useMutation(DELETE_PROFILE_MUTATION)
  const mutateAndLoading = useMutateAndLoading(deleteProfileMutation)
  const account = accountQuery.data && accountQuery.data.account
  const {
    profiles, profileLimit, isProfileAllowBasicUpsell
  } = useProfilesEntitlement(accountQuery.data)

  const isPinRequired = Boolean(
    account &&
    !hasValidPin &&
    requirePinForManageProfile(account)
  )

  /**
   * Profile deletion has three ordered stages:
   * 1. onDeleteProfile checks whether a PIN is required and ensures it is requested only once.
   * 2. deleteProfile switches to default profile first when the active profile is being deleted.
   * 3. performDeleteProfile deletes, waits for the account refetch, then makes the
   *    mutation's profile list the final cache value.
   *
   * Keeping these stages separate prevents deleting the active profile, duplicate
   * PIN prompts, and a profile-switch cache reset restoring the deleted profile.
   */
  const performDeleteProfile = useCallback(async (profileId) => {
    const deletedProfile = account.profiles.find(profile => profile.id === profileId)
    const mutationData = await mutateAndLoading('profileDelete', {
      variables: {
        id: profileId
      },
      refetchQueries: [{ query: ACCOUNT_QUERY }],
      awaitRefetchQueries: true
    })
    const updatedProfiles = mutationData?.deleteProfile?.profiles

    // The profile switch resets and refetches the Apollo cache. Write the mutation
    // result last so an in-flight account refetch cannot restore the deleted profile.
    const accountData = apolloClient.readQuery({
      query: ACCOUNT_QUERY
    })
    if (accountData?.account && updatedProfiles) {
      apolloClient.writeQuery({
        query: ACCOUNT_QUERY,
        data: {
          ...accountData,
          account: {
            ...accountData.account,
            profiles: updatedProfiles
          }
        }
      })
    }

    // Add segment data analytics for deleting profile
    if (deletedProfile) {
      segmentTrackDeleteProfile({
        id: deletedProfile.id,
        name: deletedProfile.name
      })
    }

    return mutationData
  }, [account, apolloClient, mutateAndLoading])

  const isSwitchPinRequired = useCallback((profileId) => {
    if (account.selectedProfile !== profileId) return false

    const currentProfile = account.profiles.find(profile => profile.id === profileId)
    const defaultProfile = account.profiles.find(profile => profile.isDefault)

    // This PIN protects the move from the current rating to the default profile's
    // higher rating; it is not an additional authorization for the deletion itself.
    return Boolean(
      currentProfile &&
      defaultProfile &&
      requirePinForSwitchProfile(account) &&
      hasHigherParentalRating(currentProfile.maxRating, defaultProfile.maxRating)
    )
  }, [account])

  const deleteProfile = useCallback(async (profileId) => {
    if (account.selectedProfile === profileId) {
      // Switch to default profile before user delete active profile
      const defaultProfile = account.profiles.find(profile => profile.isDefault)

      if (isSwitchPinRequired(profileId)) {
        // PIN entry is asynchronous. Continue with deletion only after the protected
        // profile switch has completed successfully.
        return dispatch(switchProfileWithPin(
          defaultProfile,
          null,
          () => performDeleteProfile(profileId)
        ))
      }

      await dispatch(switchProfile(defaultProfile))
    }

    return performDeleteProfile(profileId)
  }, [account, dispatch, isSwitchPinRequired, performDeleteProfile])

  const onAddProfile = useCallback(() => {
    const profilesAddLocation = getModalLocation(location, MODALS.qsParams.profilesAdd)
    if (isPinRequired) {
      dispatch(requirePin(() => {
        history.push(profilesAddLocation)
      }))
      return
    }
    history.push(profilesAddLocation)
  }, [dispatch, history, isPinRequired, location])

  const onEditProfile = useCallback((profileId) => {
    const profilesEditLocation = getModalLocation(location, MODALS.qsParams.profilesEdit, {
      profileId
    })
    if (isPinRequired) {
      dispatch(requirePin(() => {
        history.push(profilesEditLocation)
      }))
      return
    }
    history.push(profilesEditLocation)
  }, [dispatch, history, isPinRequired, location])

  const onCancelClick = useCallback(() => {
    setDisplayConfirmationModal(false)
  }, [])

  const displayDeleteConfirmation = useCallback((profileId) => {
    setSelectedProfileId(profileId)
    setDisplayConfirmationModal(true)
  }, [])

  const onDeleteProfile = useCallback((profileId) => {
    // The switch PIN also authorizes this flow, so do not ask for the manage PIN first.
    if (isPinRequired && !isSwitchPinRequired(profileId)) {
      dispatch(requirePin(() => {
        displayDeleteConfirmation(profileId)
      }))
      return
    }
    displayDeleteConfirmation(profileId)
  }, [dispatch, displayDeleteConfirmation, isPinRequired, isSwitchPinRequired])

  const onConfirmClick = useCallback(() => {
    deleteProfile(selectedProfileId)
    setDisplayConfirmationModal(false)
  }, [deleteProfile, selectedProfileId])

  const onDoneClick = useCallback(() => {
    if (location.state && location.state.from) {
      history.goBack()
    } else {
      history.push(location.pathname)
    }
  }, [history, location])

  if (shouldRedirect) {
    return <Redirect to={redirectTo} />
  }

  if (!account || accountQuery.loading || accountQuery.error) {
    return <LoadingModal />
  }

  const showUpgradePopup = (isProfileDisabled) => {
    if (isProfileDisabled) {
      dispatch(showPlanUpgradePopup())
    }
  }

  const onAddIconClick = () => {
    if (isProfileAllowBasicUpsell) {
      dispatch(showPlanUpgradePopup())
    } else {
      onAddProfile()
    }
  }

  return (
    <>
      <div className={styles.wrapper}>
        <div className={styles.header}>
          <h1 className={styles.title}>Manage your profile or add a new one</h1>
        </div>
        <div className={styles.profileList}>
          {profiles.map((profile, index) => {
            const onDelete = profile.isDefault ? null : () => {
              onDeleteProfile(profile.id)
            }
            const isProfileDisabled = getProfileIsDisabled(isProfileAllowBasicUpsell, profileLimit, index)

            return (
              <div
                className={styles.profileListItem}
                key={profile.id}
                onClick={() => showUpgradePopup(isProfileDisabled)}
              >
                <ProfileTile
                  profile={profile}
                  isProfileDisabled={isProfileDisabled}
                  onEditProfile={() => onEditProfile(profile.id)}
                  onDeleteProfile={onDelete}
                  isEditActive={currentActiveEdit === profile.id}
                  onActiveEdit={() => {
                    setCurrentActiveEdit(profile.id)
                  }}
                  onDeactivateEdit={() => {
                    setCurrentActiveEdit(null)
                  }}
                />
              </div>
            )
          })}
          {
            profiles.length < PROFILES.maxNumber && (
              <div className={styles.profileListItem}>
                <AddProfileButton
                  onClick={onAddIconClick}
                  isProfileAllowBasicUpsell={isProfileAllowBasicUpsell}
                />
              </div>
            )
          }
        </div>
        <Button
          onClick={onDoneClick}
          className={styles.doneButton}
          text="Done"
          secondary
        />
      </div>
      {
        isConfirmationModalOpen && (
          <ConfirmationModal
            header="Are you sure?"
            message={
              `You're about to permanently delete this profile,
              are you sure this is what you'd like to do?`
            }
            confirmText="Yes, Delete this profile"
            cancelText="Cancel"
            onCancelClick={onCancelClick}
            onConfirmClick={onConfirmClick}
          />
        )
      }
    </>
  )
}

ManageProfiles.propTypes = {
  history: PropTypes.shape({
    goBack: PropTypes.func.isRequired,
    push: PropTypes.func.isRequired
  }).isRequired,
  location: PropTypes.shape({
    pathname: PropTypes.string.isRequired,
    search: PropTypes.string.isRequired,
    state: PropTypes.shape({
      from: PropTypes.oneOfType([PropTypes.bool, PropTypes.string])
    })
  }).isRequired
}

export default ManageProfiles
